/**
 * Detección y recuperación de errores de carga de chunk (módulos lazy).
 *
 * El white-screen más común de un SPA: el usuario tiene la app abierta, se
 * despliega una versión nueva (los chunks cambian de hash), y al navegar a una
 * página lazy que aún no había cargado, el import dinámico apunta a un fichero
 * que ya no existe → rechaza. No es un bug de la app: es una versión vieja. La
 * recuperación correcta es recargar (la versión nueva sí tiene sus chunks).
 */

const RELOAD_KEY = 'pelip:chunk-reload-at';
const RELOAD_GUARD_MS = 12_000; // no recargar dos veces seguidas (evita bucle)

/** ¿El error es un fallo de carga de módulo lazy (chunk viejo tras deploy)? */
export function isChunkLoadError(error: unknown): boolean {
  const e = error as { name?: string; message?: string } | null;
  const name = e?.name ?? '';
  const msg = e?.message ?? String(error ?? '');
  return (
    name === 'ChunkLoadError' ||
    /loading chunk [\w-]+ failed/i.test(msg) ||
    /failed to (fetch|load) dynamically imported module/i.test(msg) ||
    /error loading dynamically imported module/i.test(msg) ||
    /importing a module script failed/i.test(msg)
  );
}

/**
 * Recarga FORZANDO HTML fresco: añade un parámetro `_r` efímero a la URL para
 * que ni la caché del navegador ni la del edge (Vercel) puedan devolver un
 * index.html viejo (que referenciaría chunks que ya no existen → recarga atascada).
 * Una recarga normal a veces sirve HTML cacheado y el bucle no se rompe.
 */
function forceFreshReload(): void {
  try {
    const u = new URL(window.location.href);
    u.searchParams.set('_r', Date.now().toString(36));
    window.location.replace(u.toString());
  } catch {
    window.location.reload();
  }
}

/**
 * Recarga la página UNA vez para coger la versión nueva. Guarda un sello en
 * sessionStorage para no entrar en bucle de recarga si el fallo persistiera por
 * otra causa. Devuelve true si lanzó la recarga, false si la frenó el guard.
 */
export function reloadForFreshChunk(): boolean {
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < RELOAD_GUARD_MS) return false;
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // sessionStorage puede fallar (modo privado): recargamos igual, sin guard.
  }
  forceFreshReload();
  return true;
}

/** Recarga inmediata forzando HTML fresco (para el botón "Actualizar"). */
export function reloadToLatest(): void {
  forceFreshReload();
}

/**
 * Limpia el parámetro `_r` de la URL tras el arranque (cosmético), sin recargar
 * ni romper el historial. Llamar una vez en main.tsx.
 */
export function stripReloadParam(): void {
  try {
    const u = new URL(window.location.href);
    if (u.searchParams.has('_r')) {
      u.searchParams.delete('_r');
      window.history.replaceState(window.history.state, '', u.pathname + u.search + u.hash);
    }
  } catch {
    // no pasa nada: el parámetro es inocuo para el router.
  }
}

/**
 * Registra recuperación GLOBAL ante chunks viejos en las superficies que un
 * ErrorBoundary de React no ve: el evento `vite:preloadError` (falla la precarga
 * del módulo) y las promesas rechazadas sin capturar. Llamar una vez en el arranque.
 */
export function installChunkErrorReload(): void {
  // Vite emite este evento cuando falla precargar un módulo lazy; por defecto
  // relanza el error. Lo frenamos y recargamos a la versión nueva.
  window.addEventListener('vite:preloadError', (e) => {
    e.preventDefault();
    reloadForFreshChunk();
  });
  window.addEventListener('unhandledrejection', (e) => {
    if (isChunkLoadError((e as PromiseRejectionEvent).reason)) reloadForFreshChunk();
  });
}
